<script setup lang="ts">
/* eslint-disable no-undef */
/**
 * QxReview Product Route Page
 *
 * Static route for QxReview product landing page.
 * Queries markdown content from /content/products/review.md
 * and renders using ProductPageTemplate component.
 *
 * Path: /QxReview
 * Content Source: content/products/review.md
 * Integration: Nuxt Content + ProductPageTemplate + ROIBlock + PricingBlock + BookingsCTA
 */

definePageMeta({
  path: '/QxReview',
})

import { REVIEW_ROI_FIXTURES } from '../../test/fixtures/roi-metrics.fixtures'
import { REVIEW_PRICING_MESSAGE } from '../../test/fixtures/pricing.fixtures'
import PricingBlock from '../../components/product/PricingBlock.vue'
import ROIBlock from '../../components/product/ROIBlock.vue'
import BookingsCTA from '../../components/cta/BookingsCTA.vue'
import FormFallbackCTA from '../../components/cta/FormFallbackCTA.vue'
import {
  buildBreadcrumbJsonLd,
  buildProductJsonLd,
  createJsonLdScript,
  toAbsoluteUrl,
} from '../../composables/useStructuredData'

/**
 * Access app configuration for bookings URL
 */
const appConfig = useAppConfig()

/**
 * Bookings API availability flag
 * In production, this would be connected to health check endpoint
 * For MVP: Toggle manually via browser console: window.__bookingsAvailable = false
 */
const bookingsAvailable = ref(true)

// Allow runtime override for testing (SSR-safe)
if (typeof window !== 'undefined') {
  (window as any).__bookingsAvailable = bookingsAvailable
}

/**
 * Product content interface matching markdown frontmatter schema
 */
interface ProductContent {
  title: string;
  description: string;
  image?: string;
  keywords?: string[];
  publishedAt?: string;
  body?: {
    children: Array<any>;
  };
}

/**
 * Query markdown content for QxReview product.
 * Uses useAsyncData for SSG compatibility and caching.
 * Handles prerendering context where queryContent may not be available.
 */
const { data: reviewData } = await useAsyncData('review-product', async () => {
  try {
    // @ts-expect-error - Nuxt Content auto-import
    const queryContentFn = typeof queryContent !== 'undefined' ? queryContent : null
    if (queryContentFn) {
      return await queryContentFn<ProductContent>('products/review').findOne()
    }
    return null
  } catch (err) {
    // Handle prerendering context where queryContent is not available
    console.warn('Failed to load content during prerendering:', err)
    return null
  }
});

const CANONICAL_URL = 'https://qx.qiksolve.com/QxReview';
const SOCIAL_IMAGE_FALLBACK = 'https://qx.qiksolve.com/images/products/review-hero.svg';
const SITE_ROOT = 'https://qx.qiksolve.com';
const { emitSchemaValidationPassed, emitSchemaValidationFailed } = useAnalytics();

const productImageUrl = computed(() =>
  toAbsoluteUrl(SITE_ROOT, reviewData.value?.image) ?? SOCIAL_IMAGE_FALLBACK
);

const productSchema = computed(() =>
  buildProductJsonLd({
    name: reviewData.value?.title || 'QxReview',
    description:
      reviewData.value?.description ||
      'Streamline quality document review and approval processes with QxReview.',
    url: CANONICAL_URL,
    image: productImageUrl.value,
    brandName: 'QxAIOS',
  })
);

const breadcrumbSchema = computed(() =>
  buildBreadcrumbJsonLd([
    { name: 'Home', item: `${SITE_ROOT}/` },
    { name: 'Product', item: `${SITE_ROOT}/product` },
    { name: 'QxReview', item: CANONICAL_URL },
  ])
);

if (productSchema.value.schema) {
  emitSchemaValidationPassed('product', 'product');
} else {
  emitSchemaValidationFailed('product', 'product', productSchema.value.reasonCodes);
}

if (breadcrumbSchema.value) {
  emitSchemaValidationPassed('product', 'breadcrumb');
} else {
  emitSchemaValidationFailed('product', 'breadcrumb', ['missing_breadcrumb_items']);
}

/**
 * Extract markdown sections from body AST.
 * Each section is identified by its heading text.
 */
const extractSection = (content: ProductContent | null, sectionHeading: string): string => {
  if (!content?.body?.children) return '';

  const headingIndex = content.body.children.findIndex(
    (node: any) =>
      node.tag === 'h2' &&
      node.children?.[0]?.value?.toLowerCase() === sectionHeading.toLowerCase()
  );

  if (headingIndex === -1) return '';

  // Collect nodes between this heading and next heading
  const sectionNodes = [];
  for (let i = headingIndex + 1; i < content.body.children.length; i++) {
    const node = content.body.children[i];
    if (node.tag === 'h2') break; // Stop at next section
    sectionNodes.push(node);
  }

  // Convert nodes to HTML string
  return sectionNodes
    .map((node: any) => {
      if (node.tag === 'p') {
        return `<p>${node.children?.map((c: any) => c.value || '').join('')}</p>`;
      } else if (node.tag === 'ul') {
        const items = node.children
          ?.map((li: any) => `<li>${li.children?.map((c: any) => c.value || '').join('')}</li>`)
          .join('');
        return `<ul class="list-disc list-inside space-y-2 text-neutral-700">${items}</ul>`;
      }
      return '';
    })
    .join('');
};

/**
 * SEO metadata for review product page
 * Includes Open Graph (OG), Twitter Card, and canonical URL for social sharing and SEO
 */
useSeoMeta({
  // Basic page metadata
  title: reviewData.value?.title || 'QxReview - Quality Document Review & Approval',
  description:
    reviewData.value?.description ||
    'Streamline quality document review and approval processes with QxReview.',
  keywords: reviewData.value?.keywords?.join(', '),

  // Open Graph metadata (for Facebook, LinkedIn, etc.)
  ogTitle: reviewData.value?.title || 'QxReview',
  ogDescription:
    reviewData.value?.description ||
    'Streamline quality document review and approval processes with QxReview.',
  ogImage: reviewData.value?.image
    ? `https://qx.qiksolve.com${reviewData.value.image}`
    : SOCIAL_IMAGE_FALLBACK,
  ogUrl: CANONICAL_URL,
  ogType: 'website',

  // Twitter Card metadata (for Twitter sharing)
  twitterCard: 'summary_large_image',
  twitterTitle: reviewData.value?.title || 'QxReview',
  twitterDescription:
    reviewData.value?.description ||
    'Streamline quality document review and approval processes with QxReview.',
  twitterImage: reviewData.value?.image
    ? `https://qx.qiksolve.com${reviewData.value.image}`
    : SOCIAL_IMAGE_FALLBACK,

  // Canonical URL for SEO
  canonical: CANONICAL_URL,
});

useHead({
  link: [
    {
      rel: 'canonical',
      href: CANONICAL_URL,
    },
  ],
  script: [
    ...(productSchema.value.schema ? [createJsonLdScript(productSchema.value.schema)] : []),
    ...(breadcrumbSchema.value ? [createJsonLdScript(breadcrumbSchema.value)] : []),
  ],
});
</script>

<template>
  <ProductPageTemplate class="theme-review">
    <!-- Hero Section -->
    <template #hero>
      <div class="text-center space-y-6">
        <h1 class="text-4xl sm:text-5xl md:text-6xl font-bold text-product tracking-tight">
          {{ reviewData?.title || 'QxReview' }}
        </h1>
        <p class="text-lg sm:text-xl text-neutral-600 max-w-3xl mx-auto">
          {{ reviewData?.description || 'Streamline quality document review and approval processes.' }}
        </p>
        <div
          v-if="reviewData?.image"
          class="mt-8"
        >
          <NuxtImg
            :src="reviewData.image"
            :alt="`${reviewData.title} hero illustration`"
            fetchpriority="high"
            format="webp"
            sizes="xs:100vw sm:448px"
            class="mx-auto max-w-md w-full h-auto"
          />
        </div>
        <!-- Hero CTA -->
        <div class="mt-8">
          <BookingsCTA
            v-if="bookingsAvailable"
            button-text="Start Using QxReview"
            bookings-url="/review-get-started"
            variant="primary"
            location="hero"
            product="review"
            data-product="review"
          />
          <FormFallbackCTA
            v-else
            button-text="Start Using QxReview"
            form-url="/review-get-started"
            fallback-reason="api_unavailable"
            location="hero"
            product="review"
          />
        </div>
      </div>
    </template>

    <!-- ROI Metrics Section -->
    <template #roiMetrics>
      <div class="max-w-6xl mx-auto">
        <ROIBlock
          :data="REVIEW_ROI_FIXTURES"
          product="QxReview"
        />
      </div>
    </template>

    <!-- Problem Section -->
    <template #problem>
      <div class="prose prose-lg max-w-4xl mx-auto">
        <h2 class="text-3xl font-bold text-neutral-900 mb-4">
          The Problem
        </h2>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-html="extractSection(reviewData, 'problem')" />
      </div>
    </template>

    <!-- Solution Section -->
    <template #solution>
      <div class="prose prose-lg max-w-4xl mx-auto">
        <h2 class="text-3xl font-bold text-neutral-900 mb-4">
          What You Get From the First Run
        </h2>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-html="extractSection(reviewData, 'what you get from the first run')" />
      </div>
    </template>

    <!-- How It Works Section -->
    <template #howItWorks>
      <div class="prose prose-lg max-w-4xl mx-auto">
        <h2 class="text-3xl font-bold text-neutral-900 mb-4">
          How QxReview Works
        </h2>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-html="extractSection(reviewData, 'how qxreview works')" />
        <!-- Mid-page CTA -->
        <div class="mt-8 text-center">
          <BookingsCTA
            v-if="bookingsAvailable"
            button-text="Book a Discussion"
            :bookings-url="appConfig?.bookings?.url || '/contact/'"
            variant="secondary"
            location="mid"
            product="review"
            data-product="review"
          />
          <FormFallbackCTA
            v-else
            button-text="Book a Discussion"
            form-url="/contact"
            fallback-reason="api_unavailable"
            location="mid"
            product="review"
          />
        </div>
      </div>
    </template>

    <!-- Trust + Expansion Section -->
    <template #useCases>
      <div class="prose prose-lg max-w-4xl mx-auto">
        <h2 class="text-3xl font-bold text-neutral-900 mb-4">
          Trust Architecture
        </h2>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-html="extractSection(reviewData, 'trust architecture')" />

        <h3 class="mt-8 text-2xl font-bold text-neutral-900 mb-4">
          Start Small, Grow When Ready
        </h3>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-html="extractSection(reviewData, 'start small, grow when ready')" />
      </div>
    </template>

    <!-- Pricing Transparency Section -->
    <template #cta>
      <div class="max-w-6xl mx-auto space-y-8">
        <div class="prose prose-lg max-w-4xl mx-auto">
          <h2 class="text-3xl font-bold text-neutral-900 mb-4">
            Commercial Model
          </h2>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div v-html="extractSection(reviewData, 'commercial model')" />
        </div>

        <PricingBlock
          :message="REVIEW_PRICING_MESSAGE"
          product="QxReview"
        />
        <!-- Footer CTA -->
        <div class="text-center">
          <BookingsCTA
            v-if="bookingsAvailable"
            button-text="Start Using QxReview"
            bookings-url="/review-get-started"
            variant="secondary"
            location="footer"
            product="review"
            data-product="review"
          />
          <FormFallbackCTA
            v-else
            button-text="Start Using QxReview"
            form-url="/review-get-started"
            fallback-reason="api_unavailable"
            location="footer"
            product="review"
          />
        </div>
      </div>
    </template>
  </ProductPageTemplate>
</template>
